import { FC, useState } from 'react';

import styled from '@emotion/styled';
import { ClickAwayListener, Tooltip, tooltipClasses, Typography } from '@mui/material';
import { useBreakpoints } from 'hooks';

import { TOOLTIP_MODE, TOOLTIP_VARIANT } from './constants';
import { ITooltipComponentProps, ModeToColorMap, VariantToPaddingMap } from './types';

import styles from './TooltipComponent.module.scss';

import scssVariables from 'styles/sass-variables.module.scss';

const variantPaddingMap: VariantToPaddingMap = {
  [TOOLTIP_VARIANT.SMALL]: {
    paddingTop: 2,
    paddingRight: 6,
    paddingBottom: 2,
    paddingLeft: 6,
  },
  [TOOLTIP_VARIANT.LARGE]: {
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 16,
    paddingLeft: 16,
  },
};

const modeColorMap: ModeToColorMap = {
  [TOOLTIP_MODE.DARK]: {
    color: scssVariables.white,
    backgroundColor: scssVariables.greys_text,
  },
  [TOOLTIP_MODE.LIGHT]: {
    backgroundColor: scssVariables.brand_yellow_100,
    color: scssVariables.greys_black,
  },
  [TOOLTIP_MODE.WHITE]: {
    backgroundColor: scssVariables.white,
    color: scssVariables.greys_text,
  },
};

const CustomTooltip = styled(({ className, popperOffset, ...props }: ITooltipComponentProps) => (
  <Tooltip
    {...props} // Props should come first so they don't get overridden
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: popperOffset ?? [0, 5],
          },
        },
      ],
    }}
  />
))(({ variant, mode }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 269,
    paddingTop: variantPaddingMap[variant ?? TOOLTIP_VARIANT.LARGE].paddingTop,
    paddingRight: variantPaddingMap[variant ?? TOOLTIP_VARIANT.LARGE].paddingRight,
    paddingBottom: variantPaddingMap[variant ?? TOOLTIP_VARIANT.LARGE].paddingBottom,
    paddingLeft: variantPaddingMap[variant ?? TOOLTIP_VARIANT.LARGE].paddingLeft,
    borderRadius: variant === TOOLTIP_VARIANT.SMALL ? 4 : 8,
    border: `1px solid ${scssVariables.brand_orange_900}`,
    backgroundColor: modeColorMap[mode ?? TOOLTIP_MODE.DARK].backgroundColor,
    color: modeColorMap[mode ?? TOOLTIP_MODE.DARK].color,
    fontSize: 'large',
    boxShadow: TOOLTIP_MODE.LIGHT && '0px 2px 7px 0px rgba(0, 0, 0, 0.12)',
    cursor: 'pointer', // Add cursor pointer to indicate clickable
  },
  [`& .MuiTooltip-arrow`]: {
    color: modeColorMap[mode ?? TOOLTIP_MODE.DARK].backgroundColor,
    '&::before': {
      border: `1px solid ${scssVariables.brand_orange_900}`,
    },
  },
}));

export const TooltipComponent: FC<ITooltipComponentProps> = ({
  title,
  children,
  placement = 'top-end',
  variant = TOOLTIP_VARIANT.LARGE,
  mode = TOOLTIP_MODE.LIGHT,
  isTooltipHidden,
  isTitleIsNode = false,
  className,
  leaveDelay,
  trigger = 'hover',
  popperOffset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useBreakpoints();

  if (isTooltipHidden) return children;

  const handleClick = () => {
    if (isMobile && trigger === 'click') {
      setIsOpen(!isOpen);
    }
  };

  const handleClickAway = () => {
    if (isMobile && trigger === 'click') {
      setIsOpen(false);
    }
  };

  const tooltipTitle = isTitleIsNode ? (
    <div onClick={() => setIsOpen(false)}>{title}</div>
  ) : (
    <Typography variant="subtitle2" className={styles.tooltipTitleStyles} onClick={() => setIsOpen(false)}>
      {title}
    </Typography>
  );

  const tooltipContent = (
    <CustomTooltip
      leaveDelay={leaveDelay}
      className={className}
      title={tooltipTitle}
      arrow={variant === TOOLTIP_VARIANT.LARGE}
      placement={placement}
      variant={variant}
      mode={mode}
      popperOffset={popperOffset}
      open={isMobile && trigger === 'click' ? isOpen : undefined}
      onClose={() => {
        if (isMobile && trigger === 'click') {
          setIsOpen(false);
        }
      }}
      // Disable MUI's default touch hold behavior on mobile
      disableTouchListener={isMobile && trigger === 'click'}
    >
      <div onClick={handleClick} style={{ width: 'max-content', maxWidth: 'fit-content' }}>
        {children}
      </div>
    </CustomTooltip>
  );

  // For mobile click behavior, wrap with ClickAwayListener
  if (isMobile && trigger === 'click') {
    return (
      <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleClickAway}>
        <div>{tooltipContent}</div>
      </ClickAwayListener>
    );
  }

  return tooltipContent;
};
